import React, { useLayoutEffect, useRef, useState, useEffect } from 'react';
import { DollarSign, Clock, User } from 'lucide-react';
import { triggerHapticFeedback } from '../../../utils/capacitorHelper';

const YACHANGU_LOGO = 'https://customer-assets.emergentagent.com/job_yachangu-pay/artifacts/ynseryay_EE6C3FEA-5C5E-424A-B08C-ED1F2A53B790.jpeg';

/**
 * Driver bottom nav — Bolt-style pill+circles with a sliding active pill.
 * The green pill is an absolute element that animates its `left` + `width`
 * to wrap whichever tab is active (FLIP-style Magic Move).
 *
 * Home tab uses the Yachangu logo (matches rider app).
 */
const TABS = [
  { id: 'home', label: 'Home', icon: 'logo' },
  { id: 'earnings', label: 'Earnings', icon: DollarSign },
  { id: 'history', label: 'History', icon: Clock },
  { id: 'account', label: 'Account', icon: User },
];

const DriverBottomNav = ({ activeTab, onTabChange }) => {
  const buttonRefs = useRef({});
  const [pill, setPill] = useState({ left: 0, width: 0, opacity: 0 });

  const handleTabPress = (id) => {
    if (id === activeTab) return;
    triggerHapticFeedback('light');
    onTabChange(id);
  };

  const measure = () => {
    const el = buttonRefs.current[activeTab];
    if (!el) return;
    setPill({ left: el.offsetLeft, width: el.offsetWidth, opacity: 1 });
  };

  // Position the pill on every tab change + initial mount.
  // Re-measure on the next frame too, so late layout (e.g. web-font load
  // widening the active label) doesn't leave the label spilling out of the pill.
  useLayoutEffect(() => {
    measure();
    const raf = requestAnimationFrame(measure);
    return () => cancelAnimationFrame(raf);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab]);

  // Re-measure once the web fonts are ready (label width changes on font swap).
  useEffect(() => {
    if (typeof document !== 'undefined' && document.fonts && document.fonts.ready) {
      document.fonts.ready.then(measure).catch(() => {});
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab]);

  // Keep it in sync with resize / safe-area changes
  useEffect(() => {
    const onResize = () => measure();
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab]);

  return (
    <nav
      data-testid="driver-bottom-nav"
      className="fixed bottom-3 left-3 right-3 z-[55]"
      style={{ paddingBottom: 'env(safe-area-inset-bottom, 0)' }}
    >
      {/* Clear glassy container — transparent with a wet, shiny reflection (no fog/blur) */}
      <div
        className="relative flex items-center justify-between gap-2 px-2 py-2 rounded-full border border-white/50 shadow-[0_12px_40px_-8px_rgba(16,185,129,0.28),0_2px_6px_rgba(0,0,0,0.06)] overflow-hidden"
        style={{
          background: 'linear-gradient(135deg, rgba(255,255,255,0.90) 0%, rgba(255,255,255,0.75) 42%, rgba(255,255,255,0.83) 100%)',
        }}
      >
        {/* Top inner highlight for a glassy edge */}
        <div
          aria-hidden="true"
          className="pointer-events-none absolute inset-x-3 top-0 h-px bg-gradient-to-r from-transparent via-white/80 to-transparent"
        />
        {/* Specular shine — soft diagonal reflection for the wet/water look */}
        <div
          aria-hidden="true"
          className="pointer-events-none absolute -top-6 -left-8 w-2/3 h-16 rotate-12 rounded-full"
          style={{ background: 'radial-gradient(closest-side, rgba(255,255,255,0.45), rgba(255,255,255,0))' }}
        />
        {/* Sliding pill with emerald glow ring (iOS-inspired) */}
        <span
          data-testid="driver-nav-pill"
          aria-hidden="true"
          className="absolute top-2 bottom-2 rounded-full border border-emerald-500/40 shadow-[0_0_22px_-4px_rgba(16,185,129,0.65)]"
          style={{
            left: pill.left,
            width: pill.width,
            opacity: pill.opacity,
            background: 'linear-gradient(135deg, rgba(16,185,129,0.28) 0%, rgba(16,185,129,0.12) 100%)',
            transition: 'left 320ms cubic-bezier(0.2, 0.8, 0.2, 1), width 320ms cubic-bezier(0.2, 0.8, 0.2, 1), opacity 200ms ease-out',
          }}
        />

        {TABS.map(({ id, label, icon }) => {
          const isActive = activeTab === id;
          const isLogo = icon === 'logo';
          const Icon = !isLogo ? icon : null;
          return (
            <button
              key={id}
              type="button"
              ref={(el) => { buttonRefs.current[id] = el; }}
              onClick={() => handleTabPress(id)}
              data-testid={`driver-tab-${id}`}
              aria-pressed={isActive}
              aria-label={label}
              className={`relative z-10 h-12 rounded-full inline-flex items-center justify-center gap-2 transition-[background-color,color,box-shadow,transform] duration-200 ${
                isActive
                  ? 'text-emerald-900 font-semibold text-[14px] px-5'
                  : 'text-gray-700 bg-white/45 border border-white/60 hover:bg-white/65 active:scale-95 w-12 shrink-0 backdrop-blur-sm shadow-[inset_0_1px_0_rgba(255,255,255,0.7),0_1px_2px_rgba(0,0,0,0.04)]'
              }`}
            >
              {isLogo ? (
                <span
                  className={`w-6 h-6 rounded-md overflow-hidden flex-shrink-0 transition-transform ${
                    isActive ? 'ring-2 ring-emerald-500/40 scale-105' : ''
                  }`}
                >
                  <img src={YACHANGU_LOGO} alt="Yachangu" className="w-full h-full object-cover" />
                </span>
              ) : (
                <Icon
                  className={`w-5 h-5 ${isActive ? 'text-emerald-700' : ''}`}
                  strokeWidth={isActive ? 2.4 : 2}
                />
              )}
              {isActive && <span className="ml-0.5">{label}</span>}
            </button>
          );
        })}
      </div>
    </nav>
  );
};

export default DriverBottomNav;
